<template>
  <Stack>

    <Dropzone
      :multiple="true"
      :onChange="sendFilesToBackend"
      :progress="uploadStore.uploadProgress"
       @reset-progress="uploadStore.uploadProgress = 0"
    />
    <div class="howto">
      <div class="howto-content">
        <p><strong>Get the Best Results from Your Uploads</strong></p>
        <ul class="info-list">
          <li>📄 <strong>Upload one document per file</strong></li>
          <li>🔄 <strong>Ensure pages are in correct order & not rotated</strong></li>
          <li>📷 <strong>Use a high-quality scan</strong> for clarity</li>
          <li>✅ <strong>Optimized extraction</strong> for: W-2s, 1099s, 1040s, ID documents, and more</li>
        </ul>
      </div>

      <button class="howto-button stk-button stk-size-3_5" @click="showModal = true">Learn More</button>
    </div>

    <Stack v-if="uploadStore.currentSessionUploads.length || uploadStore.isUploading">
      <Title size="5">
        Current Upload
      </Title>

      <LoaderWithText v-if="uploadStore.isUploading">
        Processing…
      </LoaderWithText>

      <template v-else-if="uploadStore.currentSessionUploads.length">
        <p v-if="isModal">
          While the files are processing, feel free to close this modal.
          You can see their status on the <strong>Documents</strong> tab.
        </p>
        <DocumentTable
          :documents="uploadStore.currentSessionUploads"
          :entityExternalId="entityExternalId"
          :key="forceRerender"
          mode="advisor"
        />
      </template>
    </Stack>

    <!-- Learn More Modal -->
    <div class="modal" :class="{ 'is-active': showModal }">
      <div class="modal-background" @click="showModal = false"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Supported Document Types</p>
          <button class="delete" @click="showModal = false"></button>
        </header>
        <section class="modal-card-body">
          <p><strong>We extract information from any document! We currently optimize extraction for these document types:</strong></p>
          <ul>
            <li>📝 W-2, W-4, 1040, 1098, 1099 tax documents</li>
            <li>🆔 ID documents (Driver's license, passport, state ID)</li>
            <li>📜 Brokerage statements </li>
          </ul>
          <p><strong>Supported File Types:</strong></p>
          <ul>
            <li>PDF</li>
            <li>PNG, JPEG/JPG, GIF, BMP, TIFF, PCX, PPM, APNG, PSD, CUR, DCX, FTEX, HEIC</li>
            <li>CSV, XLSX, XLSM, XLS, XLTX, XLTM, QPW </li>
            <li>PPTX, PPT, DOCX, DOC, DOTX, WPD, TXT, RTF</li>
          </ul>
          <p class="mt-3">
            We are continuously improving our extraction for more document types, check back soon!
          </p>
        </section>
        <footer class="modal-card-foot">
          <button class="button is-link" @click="showModal = false">Got it!</button>
        </footer>
      </div>
    </div>
  </Stack>
</template>

<script setup>
import { Dropzone, LoaderWithText, Stack, Title } from '@stakeui';
import { ref, onMounted, onUnmounted } from 'vue';
import DocumentTable from './DocumentTable.vue';
import { useDocumentUploadStore } from '@frontend/ui/stores/DocmasterStore';

const props = defineProps({
  entityExternalId: {
    type: String,
    required: true,
  },
  csrfToken: {
    type: String,
    required: true,
  },
  isModal: {
    type: Boolean,
  },
});

const emit = defineEmits(['documents-updated']);
const forceRerender = ref(0);

// Initialize the store
const uploadStore = useDocumentUploadStore();
const showModal = ref(false);

// Store event unsubscribe functions
const unsubscribeFunctions = [];

onMounted(async () => {
  // Configure store for advisor mode
  uploadStore.setMode('advisor');
  uploadStore.updateConfig({ 
    csrfToken: props.csrfToken,
    pollUntilFullyProcessed: true,
    enableDetailedErrors: true,
    autoChecklistCrossOff: false
  });

  const unsubscribeSessionUpdate = uploadStore.on('onSessionDocumentUpdated', (data) => {
  console.log('Session document updated:', data);
  // Force component reactivity update
  forceRerender.value++;
});
  
  // Watch for changes
  uploadStore.on('onUploadComplete', (data) => {
    emit('documents-updated');
  });

  // Set current entity and fetch past documents
  await uploadStore.setCurrentEntityWithHistory(props.entityExternalId);

  // Set up event listeners
  const unsubscribeUploadComplete = uploadStore.on('onUploadComplete', (data) => {
    console.log('Upload completed:', data);
    emit('documents-updated');
  });

  const unsubscribeDocumentCompleted = uploadStore.on('onDocumentCompleted', (data) => {
    console.log('Document processing completed:', data);
    emit('documents-updated');
  });

  const unsubscribeBatchComplete = uploadStore.on('onBatchComplete', (data) => {
    console.log('Batch processing completed:', data);
    emit('documents-updated');
  });

  const unsubscribeError = uploadStore.on('onError', (data) => {
    console.error('Upload/processing error:', data);
    // Could show user-facing error message here if needed
  });

  const unsubscribeInvalidFilesIgnored = uploadStore.on('onInvalidFilesIgnored', (data) => {
    const fileNames = data.files.map(f => f.name).join(', ');
    alert(`The following files are not valid file types and won't be processed: ${fileNames}`);
  });

  // Store unsubscribe functions for cleanup
  unsubscribeFunctions.push(
    unsubscribeUploadComplete,
    unsubscribeDocumentCompleted,
    unsubscribeBatchComplete,
    unsubscribeError,
    unsubscribeInvalidFilesIgnored,
    unsubscribeSessionUpdate 
  );
});

onUnmounted(() => {
  // Clean up event listeners
  unsubscribeFunctions.forEach(unsubscribe => unsubscribe());
});

const sendFilesToBackend = async (files) => {

  console.log('About to upload files:', files.length)

  try {
    await uploadStore.uploadFiles(files, props.entityExternalId, null, props.csrfToken);
    console.log('Upload finished - currentSessionUploads:', uploadStore.currentSessionUploads)
  } catch (error) {
    console.error('Upload failed:', error);
    // Error handling is done through store events
  }
};
</script>

<style scoped>
.howto {
  background-color: var(--stk-success-90);
  color: var(--stk-success-30);
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 1em;
  padding: 1em 1.25em;

  strong {
    color: inherit;
    font-weight: 600;
  }
}

.howto-button {
  flex-shrink: 0;
}

.modal.is-active {
  z-index: 1000;
}

.modal-background {
  background-color: rgba(10, 10, 10, 0.86);
}
</style>
